import { useEffect, useState } from 'react';

/**
 * Debounce a value so a query fires per PAUSE, not per keystroke.
 *
 * Deliberately debounces the VALUE rather than the input: the text field stays fully
 * controlled and mounted, and only the derived query lags behind. That is what lets a
 * search box keep its results on screen while the next request is in flight, instead of
 * blanking the page on every keystroke — the stable-search-input rule in CLAUDE.md.
 *
 * Extracted from the old per-association documents pane when portfolio search (#404) needed
 * the same behaviour; a second copy would have been the start of two debounce intervals
 * that drift apart. (That pane is gone — `app/documents/index.tsx` is the search surface
 * now — but the reason for sharing one implementation is unchanged.)
 */
export function useDebounced<T>(value: T, delayMs = 350): T {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delayMs);
		return () => clearTimeout(timer);
	}, [value, delayMs]);
	return debounced;
}
